import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import SkuPositionForm from '../SkuPositionForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import translations from './translations';

const UpdateSKUPositionDrawer = ({ isOpen, onClose, onUpdate, fulfilmentLocation }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Handle update the position of the product.
   * @param {string} sku
   * @param {string} position
   * @param {boolean} addAnotherProduct
   * @returns {Promise<*>}
   */
  const handleUpdateSKUPosition = async ({ sku, position, addAnotherProduct }) => {
    try {
      let response;

      const positionsResponse = await fulfilmentApi.listPositions(fulfilmentLocation.id, {
        headers: {
          'x-page-size': 10000,
        },
      });
      const currentPositions = (positionsResponse?.data ?? []).map(item => item.code);
      const currentPositionIndex = (positionsResponse.data ?? []).findIndex(
        item => item.code === position,
      );

      if (sku) {
        response = await fulfilmentApi.upsertLocationSkus(fulfilmentLocation.id, [
          {
            sku,
            position,
          },
        ]);
      }

      if (!sku || currentPositionIndex < 0) {
        response = await fulfilmentApi.updatePositionOrder(fulfilmentLocation.id, [
          ...(positionsResponse?.data ?? []).map(({ code }) => code),
          position,
        ]);
      } else if (sku && currentPositionIndex >= 0) {
        response = await fulfilmentApi.updatePositionOrder(fulfilmentLocation.id, currentPositions);
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.skuPositionUpdated), {
        variant: 'success',
      });

      onUpdate(addAnotherProduct);

      return response.data;
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.skuPositionNotUpdated), {
        variant: 'error',
      });
      return Promise.reject(err);
    }
  };

  return (
    <FormDrawer
      title={intl.formatMessage(translations.title)}
      isOpen={isOpen}
      onClose={onClose}
      drawerWidth="400px"
    >
      <SkuPositionForm onCancel={onClose} onSubmit={handleUpdateSKUPosition} />
    </FormDrawer>
  );
};

UpdateSKUPositionDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdate: PropTypes.func,
  fulfilmentLocation: PropTypes.object.isRequired,
};

UpdateSKUPositionDrawer.defaultProps = {
  isOpen: false,
  onClose: undefined,
  onUpdate: undefined,
};

export default UpdateSKUPositionDrawer;
